import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import GameLayout from "../components/GameLayout";
import { useAuth } from "../contexts/AuthContext";
import API from "../lib/api";
import { toast } from "sonner";
import { ShoppingCart, Zap, Users, Pickaxe, Package, Star, Coins, ArrowRight, ChevronRight, UserPlus } from "lucide-react";
import GameSounds from "../lib/sounds";

export default function ShopPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [packs, setPacks] = useState([]);
  const [coinPkgs, setCoinPkgs] = useState([]);
  const [tiers, setTiers] = useState([]);
  const [tab, setTab] = useState("upgrades");
  const [loading, setLoading] = useState("");
  const [paySource, setPaySource] = useState("deposit");
  const [walletInfo, setWalletInfo] = useState({});

  useEffect(() => {
    Promise.all([
      API.get("/shop/packs").then(r => setPacks(r.data.packs)),
      API.get("/shop/coin-packages").then(r => setCoinPkgs(r.data.packages)),
      API.get("/wallet").then(r => setWalletInfo(r.data)),
      API.get("/worker-tiers").then(r => setTiers(r.data.tiers)),
    ]).catch(() => {});
  }, []);

  const buyPack = async (packId) => {
    const pack = packs.find(p => p.pack_id === packId);
    if (pack && (user?.coins || 0) < pack.price) {
      toast.error("Not enough coins! Buy coins or deposit first.");
      setTab("coins");
      return;
    }
    setLoading(packId);
    try {
      const { data } = await API.post("/shop/buy", { pack_id: packId });
      toast.success(data.message);
      GameSounds.purchase();
    } catch (e) {
      const msg = e.response?.data?.detail || "Failed";
      if (msg.includes("Not enough coins")) {
        toast.error("Not enough coins! Buy some first.");
        setTab("coins");
      } else { toast.error(msg); }
    } finally { setLoading(""); }
  };

  const buyCoinPkg = async (pkgId) => {
    setLoading(pkgId);
    try {
      const { data } = await API.post("/shop/buy-coins", { package_id: pkgId, payment_source: paySource });
      toast.success(data.message);
      GameSounds.purchase();
      API.get("/wallet").then(r => setWalletInfo(r.data)).catch(() => {});
    } catch (e) {
      const msg = e.response?.data?.detail || "Failed";
      if (msg.includes("Insufficient deposit") || msg.includes("Insufficient balance")) {
        toast.error("Insufficient balance. Redirecting to deposit...");
        setTimeout(() => navigate("/wallet"), 1500);
      } else { toast.error(msg); }
    } finally { setLoading(""); }
  };

  const hireWorker = async (tierId) => {
    setLoading(`hire-${tierId}`);
    try {
      const { data } = await API.post("/hire-worker", { tier_id: tierId });
      toast.success(data.message);
      GameSounds.purchase();
    } catch (e) {
      const msg = e.response?.data?.detail || "Failed";
      if (msg.includes("Not enough coins")) {
        toast.error("Not enough coins!");
        setTab("coins");
      } else { toast.error(msg); }
    } finally { setLoading(""); }
  };

  const PACK_COLORS = ["#00F0FF", "#FFD600", "#B026FF", "#10B981"];

  return (
    <GameLayout title="Shop">
      <div className="space-y-6" data-testid="shop-page">
        {/* Tab selector */}
        <div className="flex gap-1 bg-[#131722] rounded-lg p-1 border border-[#2A3241]/40">
          <button onClick={() => setTab("upgrades")} data-testid="shop-tab-upgrades"
            className={`flex-1 py-2.5 text-xs font-bold uppercase tracking-widest rounded-md transition-all ${tab === "upgrades" ? "bg-[#00F0FF]/10 text-[#00F0FF]" : "text-[#64748B]"}`}>
            Upgrade Packs
          </button>
          <button onClick={() => setTab("hire")} data-testid="shop-tab-hire"
            className={`flex-1 py-2.5 text-xs font-bold uppercase tracking-widest rounded-md transition-all flex items-center justify-center gap-1.5 ${tab === "hire" ? "bg-[#10B981]/10 text-[#10B981]" : "text-[#64748B]"}`}>
            <UserPlus className="w-3.5 h-3.5" /> Hire Workers
          </button>
          <button onClick={() => setTab("coins")} data-testid="shop-tab-coins"
            className={`flex-1 py-2.5 text-xs font-bold uppercase tracking-widest rounded-md transition-all flex items-center justify-center gap-1.5 ${tab === "coins" ? "bg-[#FFD600]/10 text-[#FFD600]" : "text-[#64748B]"}`}>
            <Coins className="w-3.5 h-3.5" /> Buy Coins
          </button>
        </div>

        {tab === "upgrades" && (
          <>
            <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-4 flex items-center justify-between">
              <div>
                <p className="text-sm font-bold text-[#F8FAFC]">Your balance: <span className="font-mono-game text-[#FFD600]">{user?.coins?.toFixed(0) || 0} coins</span></p>
                <p className="text-xs text-[#64748B]">Need more coins? <button onClick={() => setTab("coins")} className="text-[#FFD600] hover:underline">Buy coins</button> or <button onClick={() => navigate("/wallet")} className="text-[#00F0FF] hover:underline">deposit</button></p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {packs.map((pack, i) => {
                const color = PACK_COLORS[i % PACK_COLORS.length];
                const canAfford = (user?.coins || 0) >= pack.price;
                return (
                  <div key={pack.pack_id} className={`bg-[#131722] rounded-2xl border overflow-hidden group transition-all duration-300 hover:-translate-y-1 flex flex-col ${canAfford ? "border-[#2A3241]/40" : "border-[#EF4444]/20 opacity-80"}`} data-testid={`pack-${pack.pack_id}`}>
                    <div className="h-36 bg-[#0B0E14] relative overflow-hidden flex items-center justify-center">
                      {pack.image_url ? <img src={pack.image_url} alt={pack.name} className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-500" /> : <Package className="w-12 h-12" style={{ color }} />}
                      <div className="absolute inset-0 bg-gradient-to-t from-[#131722] to-transparent" />
                      <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full text-xs font-mono-game font-bold" style={{ backgroundColor: `${color}20`, color }}>{pack.price}c</div>
                    </div>
                    <div className="p-4 flex-1 flex flex-col">
                      <h3 className="text-sm font-bold text-[#F8FAFC] mb-1">{pack.name}</h3>
                      <p className="text-xs text-[#94A3B8] mb-3 flex-1">{pack.description}</p>
                      {/* Pack contents */}
                      <div className="space-y-1 mb-3">
                        {pack.items?.workers > 0 && <p className="text-[10px] text-[#00F0FF]"><Users className="w-3 h-3 inline mr-1" />+{pack.items.workers} {pack.items.worker_tier ? tiers.find(t => t.tier_id === pack.items.worker_tier)?.name || pack.items.worker_tier : "Gold"} Worker{pack.items.workers > 1 ? "s" : ""}</p>}
                        {pack.items?.energy > 0 && <p className="text-[10px] text-[#10B981]"><Zap className="w-3 h-3 inline mr-1" />+{pack.items.energy} Energy</p>}
                        {pack.items?.efficiency > 0 && <p className="text-[10px] text-[#B026FF]"><Star className="w-3 h-3 inline mr-1" />+{pack.items.efficiency} Efficiency</p>}
                        {pack.items?.coins > 0 && <p className="text-[10px] text-[#FFD600]"><Coins className="w-3 h-3 inline mr-1" />+{pack.items.coins} Bonus Coins</p>}
                        {pack.items?.tool && <p className="text-[10px] text-[#F59E0B]"><Pickaxe className="w-3 h-3 inline mr-1" />{pack.items.tool}{(pack.items.tool_qty || 1) > 1 ? ` x${pack.items.tool_qty}` : ""}</p>}
                        {pack.items?.shoes && <p className="text-[10px] text-[#3B82F6]"><ChevronRight className="w-3 h-3 inline mr-1" />{pack.items.shoes}{(pack.items.shoes_qty || 1) > 1 ? ` x${pack.items.shoes_qty}` : ""}</p>}
                        {pack.items?.machine && <p className="text-[10px] text-[#B026FF]"><Package className="w-3 h-3 inline mr-1" />{pack.items.machine}{(pack.items.machine_qty || 1) > 1 ? ` x${pack.items.machine_qty}` : ""}</p>}
                        {pack.items?.bag && <p className="text-[10px] text-[#10B981]"><ShoppingCart className="w-3 h-3 inline mr-1" />{pack.items.bag}{(pack.items.bag_qty || 1) > 1 ? ` x${pack.items.bag_qty}` : ""}</p>}
                      </div>
                      <button onClick={() => buyPack(pack.pack_id)} disabled={loading === pack.pack_id} data-testid={`buy-${pack.pack_id}`}
                        className={`w-full py-2 text-xs font-bold uppercase tracking-widest rounded-xl transition-all ${canAfford ? `hover:scale-[1.02]` : ""}`}
                        style={{ backgroundColor: canAfford ? `${color}20` : "#EF444420", color: canAfford ? color : "#EF4444", border: `1px solid ${canAfford ? `${color}40` : "#EF444440"}` }}>
                        {loading === pack.pack_id ? "..." : canAfford ? "Buy Pack" : "Need More Coins"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {tab === "hire" && (
          <>
            <div className="bg-[#131722] rounded-xl border border-[#2A3241]/40 p-4">
              <div className="flex items-center gap-3">
                <UserPlus className="w-5 h-5 text-[#10B981]" />
                <div>
                  <p className="text-sm font-bold text-[#F8FAFC]">Hire Workers</p>
                  <p className="text-xs text-[#94A3B8]">Choose a tier based on your budget. Higher tiers earn more.</p>
                </div>
                <p className="ml-auto text-sm font-mono text-[#FFD600]">{user?.coins?.toFixed(0) || 0}c</p>
              </div>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {tiers.map(t => {
                const canAfford = (user?.coins || 0) >= t.cost;
                const net = t.base_rate - t.food_cost;
                const roi = net > 0 ? Math.round(t.cost / net) : Infinity;
                return (
                  <div key={t.tier_id} className={`bg-[#131722] rounded-2xl border overflow-hidden transition-all hover:-translate-y-1 ${canAfford ? "border-[#2A3241]/40" : "border-[#EF4444]/20 opacity-80"}`} data-testid={`tier-${t.tier_id}`}>
                    <div className="h-3 w-full" style={{ backgroundColor: t.color }} />
                    <div className="p-5">
                      <div className="flex items-center gap-3 mb-4">
                        <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${t.color}15`, border: `2px solid ${t.color}40` }}>
                          <Users className="w-6 h-6" style={{ color: t.color }} />
                        </div>
                        <div>
                          <h3 className="text-base font-bold text-[#F8FAFC]">{t.name}</h3>
                          <p className="font-mono text-xs" style={{ color: t.color }}>{t.tier_id.toUpperCase()} TIER</p>
                        </div>
                      </div>
                      <div className="space-y-2 mb-4">
                        <div className="flex justify-between text-xs">
                          <span className="text-[#64748B]">Earning Rate</span>
                          <span className="font-mono text-[#00F0FF]">{t.base_rate}c/day</span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-[#64748B]">Food Cost</span>
                          <span className="font-mono text-[#EF4444]">-{t.food_cost}c/day</span>
                        </div>
                        <div className="flex justify-between text-xs border-t border-[#2A3241]/30 pt-2">
                          <span className="text-[#64748B]">Net Profit</span>
                          <span className="font-mono font-bold" style={{ color: net > 0 ? "#10B981" : "#EF4444" }}>{net}c/day</span>
                        </div>
                        <div className="flex justify-between text-xs">
                          <span className="text-[#64748B]">ROI</span>
                          <span className="font-mono text-[#FFD600]">{roi === Infinity ? "∞" : `${roi} days`}</span>
                        </div>
                      </div>
                      <div className="text-center mb-3">
                        <p className="font-mono text-2xl font-bold" style={{ color: t.color }}>{t.cost.toLocaleString()}c</p>
                      </div>
                      <button onClick={() => hireWorker(t.tier_id)} disabled={loading === `hire-${t.tier_id}` || !canAfford} data-testid={`hire-${t.tier_id}`}
                        className="w-full py-2.5 text-xs font-bold uppercase tracking-widest rounded-xl transition-all"
                        style={{ backgroundColor: canAfford ? `${t.color}20` : "#EF444420", color: canAfford ? t.color : "#EF4444", border: `1px solid ${canAfford ? `${t.color}40` : "#EF444440"}` }}>
                        {loading === `hire-${t.tier_id}` ? "Hiring..." : canAfford ? "Hire Worker" : "Need More Coins"}
                      </button>
                      {t.effective_message && (
                        <p className="text-[10px] text-[#64748B] text-center mt-2 italic" data-testid={`tier-message-${t.tier_id}`}>{t.effective_message}</p>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        )}

        {tab === "coins" && (
          <>
            <div className="bg-[#131722] rounded-xl border border-[#FFD600]/20 p-4">
              <div className="flex items-center justify-between flex-wrap gap-3">
                <div className="flex items-center gap-3">
                  <Coins className="w-5 h-5 text-[#FFD600]" />
                  <div>
                    <p className="text-sm font-bold text-[#F8FAFC]">Buy Coins</p>
                    <p className="text-xs text-[#94A3B8]">Purchase coins using your deposit balance.</p>
                  </div>
                </div>
                <div className="flex items-center gap-3 text-xs">
                  <span className="text-[#00F0FF] font-mono-game">Deposit: ${walletInfo?.wallet?.deposit_balance?.toFixed(2) || "0.00"}</span>
                  {walletInfo?.allow_withdraw_purchases && <span className="text-[#10B981] font-mono-game">Withdraw: ${walletInfo?.wallet?.withdrawable_balance?.toFixed(2) || "0.00"}</span>}
                </div>
              </div>
            </div>

            {/* Payment source selector */}
            <div className="flex gap-1 bg-[#0B0E14] rounded-lg p-1 border border-[#2A3241]/30">
              <button onClick={() => setPaySource("deposit")} data-testid="pay-source-deposit"
                className={`flex-1 py-2 text-xs font-bold uppercase tracking-widest rounded-md transition-all ${paySource === "deposit" ? "bg-[#00F0FF]/10 text-[#00F0FF]" : "text-[#64748B]"}`}>
                Deposit Balance (${walletInfo?.wallet?.deposit_balance?.toFixed(2) || "0"})
              </button>
              {walletInfo?.allow_withdraw_purchases && (
                <button onClick={() => setPaySource("withdraw")} data-testid="pay-source-withdraw"
                  className={`flex-1 py-2 text-xs font-bold uppercase tracking-widest rounded-md transition-all ${paySource === "withdraw" ? "bg-[#F59E0B]/10 text-[#F59E0B]" : "text-[#64748B]"}`}>
                  Withdrawable (+{((walletInfo?.withdraw_purchase_fee || 0.3) * 100).toFixed(0)}% fee)
                </button>
              )}
            </div>
            {paySource === "withdraw" && (
              <div className="bg-[#F59E0B]/10 border border-[#F59E0B]/30 rounded-lg p-3 text-xs text-[#F59E0B]">
                A {((walletInfo?.withdraw_purchase_fee || 0.3) * 100).toFixed(0)}% fee will be applied when purchasing with withdrawable balance.
              </div>
            )}

            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {coinPkgs.map((pkg) => {
                const feeRate = paySource === "withdraw" ? (walletInfo?.withdraw_purchase_fee || 0.3) : 0;
                const totalCost = pkg.price_usd + (pkg.price_usd * feeRate);
                return (
                <div key={pkg.package_id} className="bg-[#131722] rounded-2xl border border-[#FFD600]/20 p-5 flex flex-col items-center text-center hover:-translate-y-1 transition-all" data-testid={`coin-pkg-${pkg.package_id}`}>
                  <div className="w-16 h-16 rounded-full bg-[#FFD600]/10 flex items-center justify-center mb-3">
                    <Coins className="w-8 h-8 text-[#FFD600]" />
                  </div>
                  <h3 className="text-base font-bold text-[#F8FAFC] mb-1">{pkg.name}</h3>
                  <p className="font-mono-game text-2xl font-bold text-[#FFD600] mb-1">{pkg.coins?.toLocaleString()}</p>
                  {pkg.bonus > 0 && <p className="text-xs text-[#10B981] mb-1">+{pkg.bonus} bonus = {(pkg.coins + pkg.bonus).toLocaleString()} total</p>}
                  <p className="text-lg font-bold text-[#F8FAFC]">${pkg.price_usd?.toFixed(2)}</p>
                  {feeRate > 0 && <p className="text-[10px] text-[#F59E0B]">+${(pkg.price_usd * feeRate).toFixed(2)} fee = ${totalCost.toFixed(2)} total</p>}
                  <p className="text-[10px] text-[#64748B] mb-3">From {paySource === "deposit" ? "deposit" : "withdrawable"} balance</p>
                  <button onClick={() => buyCoinPkg(pkg.package_id)} disabled={loading === pkg.package_id} data-testid={`buy-coin-${pkg.package_id}`}
                    className="w-full py-2.5 text-xs font-bold uppercase tracking-widest bg-[#FFD600] text-[#0B0E14] rounded-xl hover:bg-[#FFD600]/90 disabled:opacity-50 shadow-[0_0_10px_rgba(255,214,0,0.3)]">
                    {loading === pkg.package_id ? "Processing..." : "Buy Now"}
                  </button>
                </div>
                );
              })}
            </div>
            <p className="text-xs text-[#64748B] text-center">Need funds? <button onClick={() => navigate("/wallet")} className="text-[#00F0FF] hover:underline">Deposit to your account</button>.</p>
          </>
        )}
      </div>
    </GameLayout>
  );
}
